import type { RecommendationLabClientEnvironment } from '@/models/RecommendationLab';

export interface RecommendationEnvironmentFormValue {
  temperatureC: string;
  humidityPercent: string;
  uvIndex: string;
  season: '' | 'spring' | 'summer' | 'autumn' | 'winter';
  airQualityIndex: string;
}

export interface RecommendationEnvironmentIssue {
  code: 'temperature_invalid' | 'humidity_invalid' | 'uv_invalid' | 'air_quality_index_invalid';
  message: string;
}

export const DEFAULT_RECOMMENDATION_ENVIRONMENT: RecommendationEnvironmentFormValue = {
  temperatureC: '27',
  humidityPercent: '68',
  uvIndex: '6',
  season: 'summer',
  airQualityIndex: '42',
};

function toIssue(
  code: RecommendationEnvironmentIssue['code'],
  message: string,
  rawValue: string,
  minimum: number,
  maximum: number,
): RecommendationEnvironmentIssue | undefined {
  if (rawValue.trim() === '') {
    return undefined;
  }
  const value = Number(rawValue);
  return Number.isFinite(value) && value >= minimum && value <= maximum ? undefined : { code, message };
}

function toFiniteNumber(rawValue: string): number | undefined {
  if (rawValue.trim() === '') {
    return undefined;
  }
  const value = Number(rawValue);
  return Number.isFinite(value) ? value : undefined;
}

function toNumberField<Key extends 'temperatureC' | 'humidityPercent' | 'uvIndex'>(
  key: Key,
  rawValue: string,
): Partial<Record<Key, number>> {
  const value = toFiniteNumber(rawValue);
  return value === undefined ? {} : ({ [key]: value } as Partial<Record<Key, number>>);
}

function toAirQuality(
  value: RecommendationEnvironmentFormValue,
): Pick<RecommendationLabClientEnvironment, 'airQuality'> | Record<string, never> {
  const index = toFiniteNumber(value.airQualityIndex);
  if (index === undefined) {
    return {};
  }
  return {
    airQuality: { index },
  };
}

export function collectRecommendationEnvironmentIssues(
  value: RecommendationEnvironmentFormValue,
): RecommendationEnvironmentIssue[] {
  return [
    toIssue('temperature_invalid', 'Temperature must be between -100 and 100 °C.', value.temperatureC, -100, 100),
    toIssue('humidity_invalid', 'Humidity must be between 0 and 100%.', value.humidityPercent, 0, 100),
    toIssue('uv_invalid', 'UV index must be between 0 and 20.', value.uvIndex, 0, 20),
    toIssue('air_quality_index_invalid', 'U.S. AQI must be between 0 and 500.', value.airQualityIndex, 0, 500),
  ].filter((issue): issue is RecommendationEnvironmentIssue => issue !== undefined);
}

export function buildRecommendationEnvironment(
  value: RecommendationEnvironmentFormValue,
): RecommendationLabClientEnvironment | undefined {
  const environment: RecommendationLabClientEnvironment = {
    ...toNumberField('temperatureC', value.temperatureC),
    ...toNumberField('humidityPercent', value.humidityPercent),
    ...toNumberField('uvIndex', value.uvIndex),
    ...(value.season ? { season: value.season } : {}),
    ...toAirQuality(value),
  };
  return Object.keys(environment).length > 0 ? environment : undefined;
}
